import {
	AttachmentFile,
	ContractSuretyType,
	type Dtos,
	FileType,
	SuretyAccountId,
	SuretyQuoteId,
} from "@inrev/common";
import { useQuoteAttachmentActions } from "@portal/api/attachments";
import {
	useFetchAdminBondRequestExtractions,
	useFetchAdminContractExtractions,
} from "@portal/api/bond-request/admin/";
import { useFetchBondRequestExtractions } from "@portal/api/bond-request/agent";
import { useFetchQuoteAttachments } from "@portal/api/quote";
import { useFeatureFlag } from "@portal/hooks/useFeatureFlag";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { DateTime } from "luxon";
import { FC, useMemo } from "react";
import { DynamicDataTableColumn } from "../../ui/DynamicDataTable/types";
import { ExtractionStatusIcon } from "../../uploaded-documents/ExtractionStatusIcon";
import { ContractExtraction, UploadedDocumentRow } from "../../uploaded-documents/types";
import {
	createUploadedDocumentsColumns,
	UPLOADED_DOCUMENTS_INITIAL_SORTING,
	UploadedDocumentsColumnsActionsConfig,
} from "../../uploaded-documents/UploadedDocumentsColumns";
import { mapAttachmentsToUploadedDocumentsRow } from "../../uploaded-documents/utils/mapAttachmentsToUploadedDocumentsRow";
import { AttachmentsTable } from "./AttachmentsTable";
import { mergeExtractionStatus } from "./utils/mergeExtractionStatus";

const EMPTY_ATTACHMENTS: AttachmentFile[] = [];
const EMPTY_CONTRACT_EXTRACTIONS: ContractExtraction[] = [];

export function createBondRequestDocumentsColumns({
	actions,
	extractionsByFileId,
}: {
	actions: UploadedDocumentsColumnsActionsConfig;
	extractionsByFileId: Map<string, Dtos.BondRequestExtraction.List.Response[number]>;
}): DynamicDataTableColumn<UploadedDocumentRow>[] {
	const base = createUploadedDocumentsColumns({ actions });
	const extractionColumn: DynamicDataTableColumn<UploadedDocumentRow> = {
		id: "extractionStatus",
		header: "Extraction",
		size: 100,
		enableSorting: false,
		cellStyle: { centerItems: true },
		cell: ({ row }) => {
			const merged = mergeExtractionStatus(
				extractionsByFileId.get(row.original.file.id),
				row.original.contractExtraction,
			);
			return (
				<ExtractionStatusIcon
					status={merged?.status}
					errorType={merged?.errorType}
					errorMessage={merged?.errorMessage}
					extractionCompletedAt={
						merged?.extractionCompletedAt
							? DateTime.fromISO(merged.extractionCompletedAt)
							: undefined
					}
				/>
			);
		},
	};
	return [...base, extractionColumn];
}

type BondRequestAttachmentsTableProps = {
	accountId: SuretyAccountId;
	quoteId: SuretyQuoteId;
	bondType: ContractSuretyType;
	filter?: (attachment: AttachmentFile) => boolean;
	downloadAllName: string;
	allowUpload?: boolean;
	allowDelete?: boolean;
};

type BondRequestAttachmentsTableInnerProps = BondRequestAttachmentsTableProps & {
	isAdmin: boolean;
	isExtractionEnabled: boolean;
};

type BondRequestAttachmentsTableViewProps = BondRequestAttachmentsTableInnerProps & {
	extractions: Dtos.BondRequestExtraction.List.Response | undefined;
	contractExtractions?: Dtos.ContractExtraction.Get.Response[] | undefined;
};

const AgentBondRequestAttachmentsTable: FC<BondRequestAttachmentsTableInnerProps> = (props) => {
	const { extractions } = useFetchBondRequestExtractions({
		accountId: props.accountId,
		quoteId: props.quoteId,
		enabled: props.isExtractionEnabled,
	});
	return <BondRequestAttachmentsTableView {...props} extractions={extractions} />;
};

const AdminBondRequestAttachmentsTable: FC<BondRequestAttachmentsTableInnerProps> = (props) => {
	const { extractions } = useFetchAdminBondRequestExtractions({
		accountId: props.accountId,
		quoteId: props.quoteId,
		enabled: props.isExtractionEnabled,
	});
	const { extractions: contractExtractions } = useFetchAdminContractExtractions({
		accountId: props.accountId,
		quoteId: props.quoteId,
		enabled: props.isExtractionEnabled,
	});

	return (
		<BondRequestAttachmentsTableView
			{...props}
			extractions={extractions}
			contractExtractions={contractExtractions}
		/>
	);
};

export const BondRequestAttachmentsTable: FC<BondRequestAttachmentsTableProps> = (props) => {
	const { isAdmin } = useAuthenticatedUser();
	const isExtractionEnabled = useFeatureFlag("bond_request_extraction");
	const innerProps = { ...props, isAdmin, isExtractionEnabled };
	return isAdmin ? (
		<AdminBondRequestAttachmentsTable {...innerProps} />
	) : (
		<AgentBondRequestAttachmentsTable {...innerProps} />
	);
};

const BondRequestAttachmentsTableView: FC<BondRequestAttachmentsTableViewProps> = ({
	accountId,
	quoteId,
	bondType,
	filter,
	downloadAllName,
	allowUpload = true,
	allowDelete = false,
	isAdmin,
	isExtractionEnabled,
	extractions,
	contractExtractions,
}) => {
	const isContractAiEnabled = useIsMinimalBondUIEnabled();
	const { attachments, attachmentsIsLoading } = useFetchQuoteAttachments(accountId, quoteId);
	const actionsConfig = useQuoteAttachmentActions({
		accountId,
		quoteId,
		isAdmin,
		allowDelete,
		allowUpload,
	});
	const { downloadBaseUrl, downloadQueryKeyPrefix } = actionsConfig;
	const trackedActionsConfig = useMemo(
		() => ({ ...actionsConfig, analyticsContext: "bond-request-attachments" as const, quoteId }),
		[actionsConfig, quoteId],
	);

	const extractionsByFileId = useMemo(
		() =>
			new Map(
				(extractions ?? []).flatMap((e) => (e.fileId != null ? [[e.fileId, e] as const] : [])),
			),
		[extractions],
	);

	const columns = useMemo(
		() =>
			isExtractionEnabled
				? createBondRequestDocumentsColumns({
						actions: trackedActionsConfig,
						extractionsByFileId,
					})
				: createUploadedDocumentsColumns({ actions: trackedActionsConfig }),
		[trackedActionsConfig, extractionsByFileId, isExtractionEnabled],
	);

	const rfpFileType =
		bondType === ContractSuretyType.bid ? FileType.bid_package : FileType.contract;

	const filteredAttachments = useMemo(() => {
		const effectiveFilter =
			filter ??
			(isExtractionEnabled
				? undefined
				: (a: AttachmentFile) => !a.file.types.includes(rfpFileType));
		return effectiveFilter
			? (attachments ?? EMPTY_ATTACHMENTS).filter(effectiveFilter)
			: (attachments ?? EMPTY_ATTACHMENTS);
	}, [attachments, filter, isExtractionEnabled, rfpFileType]);

	const allUploadedRows = useMemo(() => {
		const rows = mapAttachmentsToUploadedDocumentsRow(
			filteredAttachments,
			[],
			contractExtractions ?? EMPTY_CONTRACT_EXTRACTIONS,
		);
		if (!isAdmin || !isExtractionEnabled) return rows;
		return rows.map((row) => {
			const extraction = extractionsByFileId.get(row.file.id);
			if (extraction == null) return row;
			return {
				...row,
				bondRequestExtraction: { id: extraction.id, status: extraction.status },
			};
		});
	}, [filteredAttachments, contractExtractions, isAdmin, isExtractionEnabled, extractionsByFileId]);

	const downloadConfig = useMemo(
		() =>
			downloadAllName
				? {
						baseUrl: downloadBaseUrl,
						queryKeyPrefix: downloadQueryKeyPrefix,
						fileName: downloadAllName,
					}
				: undefined,
		[downloadBaseUrl, downloadQueryKeyPrefix, downloadAllName],
	);

	return (
		<AttachmentsTable
			tableStorageKey="bond-request-attachments"
			sectionName={isContractAiEnabled ? "Uploaded Documents" : "Additional Bond Attachments"}
			columns={columns}
			initialSorting={UPLOADED_DOCUMENTS_INITIAL_SORTING}
			isLoading={attachmentsIsLoading}
			downloadConfig={downloadConfig}
			rows={allUploadedRows}
			allowUpload={allowUpload}
			handleUpload={actionsConfig.handleUpload}
			filesPendingUpload={actionsConfig.filesPendingUpload}
			filesCompletedUpload={actionsConfig.filesCompletedUpload}
			analyticsContext="bond-request-attachments"
			useCardLayout
		/>
	);
};
